import type { ShareVisibility, ShareVisibilityPricing } from '@utils/shareVisibility';
import { DEFAULT_SHARE_VISIBILITY } from '@utils/shareVisibility';

type CopyFn = (en: string, de: string, hr: string) => string;

export type ShareVisibilitySettingsProps = {
	value: ShareVisibility;
	onChange: (next: ShareVisibility) => void;
	copy: CopyFn;
	disabled?: boolean;
	compact?: boolean;
};

export default function ShareVisibilitySettings({
	value,
	onChange,
	copy,
	disabled = false,
	compact = false
}: ShareVisibilitySettingsProps) {
	const setPricing = (pricing: ShareVisibilityPricing) => onChange({ ...value, pricing });
	const toggle = (key: 'timeline' | 'instructions' | 'team') => onChange({ ...value, [key]: !value[key] });

	const applyPreset = (preset: ShareVisibility) => onChange({ ...preset });

	return (
		<div className={compact ? 'mb-2' : 'mb-3'}>
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
				<div className="fw-semibold small">
					{copy('What your client will see', 'Was Ihr Kunde sieht', 'Što će klijent vidjeti')}
				</div>
				<div className="btn-group btn-group-sm" role="group" aria-label={copy('Visibility presets', 'Sichtbarkeits-Vorlagen', 'Predlošci vidljivosti')}>
					<button
						type="button"
						className="btn btn-outline-secondary"
						disabled={disabled}
						onClick={() => applyPreset(DEFAULT_SHARE_VISIBILITY)}
					>
						{copy('Full plan', 'Voller Plan', 'Cijeli plan')}
					</button>
					<button
						type="button"
						className="btn btn-outline-secondary"
						disabled={disabled}
						onClick={() =>
							applyPreset({ pricing: 'total_only', timeline: true, instructions: false, team: false })
						}
					>
						{copy('Overview', 'Überblick', 'Pregled')}
					</button>
					<button
						type="button"
						className="btn btn-outline-secondary"
						disabled={disabled}
						onClick={() =>
							applyPreset({ pricing: 'hidden', timeline: false, instructions: false, team: false })
						}
					>
						{copy('Design only', 'Nur Design', 'Samo dizajn')}
					</button>
				</div>
			</div>

			<div className="border rounded p-3 bg-light-subtle">
				<div className="mb-3">
					<div className="text-muted small mb-2">{copy('Pricing', 'Preise', 'Cijene')}</div>
					<div className="d-flex flex-column gap-1">
						<label className="form-check small mb-0">
							<input
								type="radio"
								className="form-check-input"
								name="share-pricing"
								checked={value.pricing === 'full'}
								disabled={disabled}
								onChange={() => setPricing('full')}
							/>
							<span className="form-check-label">
								{copy('Full breakdown (materials & labor)', 'Volle Aufschlüsselung (Material & Arbeit)', 'Potpuna razrada (materijali i rad)')}
							</span>
						</label>
						<label className="form-check small mb-0">
							<input
								type="radio"
								className="form-check-input"
								name="share-pricing"
								checked={value.pricing === 'total_only'}
								disabled={disabled}
								onChange={() => setPricing('total_only')}
							/>
							<span className="form-check-label">
								{copy('Total estimate only', 'Nur Gesamtschätzung', 'Samo ukupna procjena')}
							</span>
						</label>
						<label className="form-check small mb-0">
							<input
								type="radio"
								className="form-check-input"
								name="share-pricing"
								checked={value.pricing === 'hidden'}
								disabled={disabled}
								onChange={() => setPricing('hidden')}
							/>
							<span className="form-check-label">
								{copy('Hidden (use company quote instead)', 'Ausgeblendet (Firmenangebot nutzen)', 'Skriveno (koristi ponudu tvrtke)')}
							</span>
						</label>
					</div>
				</div>

				{[
					{
						key: 'timeline' as const,
						label: copy('Timeline & work plan', 'Zeitplan & Arbeitsplan', 'Vremenski plan i raspored rada')
					},
					{
						key: 'instructions' as const,
						label: copy('Implementation instructions & tools', 'Umsetzungsanweisungen & Werkzeuge', 'Upute za izvedbu i alati')
					},
					{
						key: 'team' as const,
						label: copy('Team size & skills', 'Teamgröße & Kompetenzen', 'Veličina tima i vještine')
					}
				].map(({ key, label }) => (
					<div className="form-check mb-2" key={key}>
						<input
							id={`share-vis-${key}`}
							type="checkbox"
							className="form-check-input"
							checked={value[key]}
							disabled={disabled}
							onChange={() => toggle(key)}
						/>
						<label className="form-check-label small" htmlFor={`share-vis-${key}`}>
							{label}
						</label>
					</div>
				))}
			</div>
		</div>
	);
}
